/**
 * CORE-1420 — fallback path to Agora through our Moscow box.
 *
 * Some users in Russia cannot hold Agora's direct path: the join never settles,
 * or the signaling socket keeps dropping into RECONNECTING. For them the client
 * re-joins through our proxy: `setProxyServer(<domain>)` sends signaling through
 * nginx on the Selectel box, `setTurnServer([...])` sends media through coturn on
 * the same box. Measured before building (Jira CORE-1420, comment 53990): the
 * Selectel → Agora leg is clean on TLS, on a 6-minute signaling socket and on
 * signed UDP.
 *
 * Who gets it is decided on the backend: `GET /api/rtc/proxy-config` answers only
 * when the `rtc_ru_proxy` flag is on for the caller (204 otherwise), so nothing
 * here can switch anyone onto the proxy by itself.
 *
 * When a client goes through the proxy:
 *  - a join that failed with a network-shaped error (our own join deadline
 *    included) is retried once through the proxy;
 *  - the signaling socket fell into RECONNECTING {@link RECONNECT_STORM_COUNT}
 *    times within {@link RECONNECT_STORM_WINDOW_MS} — the screen rebuilds the
 *    connection through the proxy;
 *  - either of those happened in the last {@link RTC_PROXY_STICKY_MS}: the next
 *    join starts on the proxy straight away instead of failing again first;
 *  - dev/staging: forced from the browser console (`agapeRtcProxy.force()`).
 * A proxied join that fails too clears the memory and goes back to direct, so
 * a broken proxy can never pin anyone to it.
 */
import { Platform } from 'react-native';
import { api } from '../api';
import { storage } from '../../utils/storage';
import { isRtcJoinTimeout } from './joinDeadline';

export type RtcProxyIceServer = { urls: string[]; username: string; credential: string };
export type RtcProxyConfig = { signalingDomain: string; iceServers: RtcProxyIceServer[]; expiresAtUtc: string };

export type RtcProxyReason = 'forced' | 'remembered' | 'join_failed' | 'reconnect_storm';

/** Local memory of «the direct path failed here». */
export type RtcProxyLocalState = {
  /** ms epoch until which joins start on the proxy. */
  rememberedUntil?: number;
  /** dev/staging console switch. */
  forced?: boolean;
  /** dev/staging: media only via our TURN (no direct/host candidates). */
  relayOnly?: boolean;
};

export const RTC_PROXY_STICKY_MS = 24 * 60 * 60 * 1000;
export const RECONNECT_STORM_COUNT = 2;
export const RECONNECT_STORM_WINDOW_MS = 5 * 60 * 1000;
/** Fetch a new config this long before its TURN credentials expire. */
const CONFIG_REFRESH_MARGIN_MS = 10 * 60 * 1000;
/** After a 204, don't ask again for this long: most users never get the proxy. */
const NO_CONFIG_RETRY_MS = 5 * 60 * 1000;

// ─── Pure decisions ──────────────────────────────────────────────────────────

/**
 * Error codes that say «the path is broken», as opposed to «the request was
 * wrong». A bad token, a UID conflict or our own leave must not send anyone to
 * the proxy — it would fail there the same way and hide the real cause.
 */
const PATH_ERROR_CODES = new Set([
  'NETWORK_ERROR',
  'NETWORK_TIMEOUT',
  'NETWORK_RESPONSE_ERROR',
  'WS_ERR',
  'WS_DISCONNECT',
  'CAN_NOT_GET_GATEWAY_SERVER',
]);

export function isPathFailure(err: unknown): boolean {
  if (isRtcJoinTimeout(err)) return true;
  const code = (err as { code?: unknown } | null)?.code;
  if (typeof code !== 'string' || !PATH_ERROR_CODES.has(code)) return false;
  // CAN_NOT_GET_GATEWAY_SERVER also covers a rejected token / app id.
  const message = String((err as { message?: unknown }).message ?? '');
  return !(code === 'CAN_NOT_GET_GATEWAY_SERVER' && /token|dynamic key|appid|app id|invalid vendor/i.test(message));
}

/** Drops entries outside the window and reports whether what is left is a storm. */
export function recordReconnect(timestamps: number[], now: number): { timestamps: number[]; storm: boolean } {
  const kept = [...timestamps, now].filter(t => now - t <= RECONNECT_STORM_WINDOW_MS);
  return { timestamps: kept, storm: kept.length >= RECONNECT_STORM_COUNT };
}

/** Should the next join start on the proxy, and why. */
export function initialProxyReason(state: RtcProxyLocalState, now: number): RtcProxyReason | null {
  if (state.forced) return 'forced';
  if (typeof state.rememberedUntil === 'number' && state.rememberedUntil > now) return 'remembered';
  return null;
}

export function isConfigFresh(config: RtcProxyConfig | null, now: number): config is RtcProxyConfig {
  if (!config) return false;
  const expires = Date.parse(config.expiresAtUtc);
  return Number.isFinite(expires) && expires - now > CONFIG_REFRESH_MARGIN_MS;
}

// ─── State ───────────────────────────────────────────────────────────────────

let cachedConfig: RtcProxyConfig | null = null;
let noConfigUntil = 0;
const listeners = new Set<() => void>();

export async function readRtcProxyState(): Promise<RtcProxyLocalState> {
  return (await storage.getRtcProxyState().catch(() => null)) ?? {};
}

async function writeRtcProxyState(next: RtcProxyLocalState): Promise<void> {
  await storage.setRtcProxyState(next).catch(() => undefined);
}

/** The config for this caller, or null when the backend does not offer the proxy. */
export async function fetchRtcProxyConfig(now = Date.now()): Promise<RtcProxyConfig | null> {
  if (isConfigFresh(cachedConfig, now)) return cachedConfig;
  const state = await readRtcProxyState();
  if (!state.forced && now < noConfigUntil) return null;
  try {
    cachedConfig = await api.getRtcProxyConfig(!!state.forced);
  } catch {
    cachedConfig = null;
  }
  noConfigUntil = cachedConfig ? 0 : now + NO_CONFIG_RETRY_MS;
  return cachedConfig;
}

/** The direct path failed: start the next joins on the proxy for a while. */
export async function rememberRtcProxy(now = Date.now()): Promise<void> {
  const state = await readRtcProxyState();
  await writeRtcProxyState({ ...state, rememberedUntil: now + RTC_PROXY_STICKY_MS });
}

/** The proxy failed too: back to direct, and drop the cached config. */
export async function forgetRtcProxy(): Promise<void> {
  const state = await readRtcProxyState();
  cachedConfig = null;
  await writeRtcProxyState({ forced: state.forced, relayOnly: state.relayOnly });
}

/** Screens that hold a live connection rebuild it when the console switch flips. */
export function onRtcProxyModeChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

// ─── Applying it to a client ─────────────────────────────────────────────────

type ProxyCapableClient = {
  setProxyServer?: (domain: string) => void;
  setTurnServer?: (config: unknown) => void;
};

/**
 * Routes a DISCONNECTED client through the proxy. Returns false when the client
 * cannot take it (the e2e fake client, or an SDK build without the methods) —
 * the caller then stays direct.
 *
 * `setTurnServer` gets the standard WebRTC array: in 4.24.2 the SDK's own
 * `{ turnServerURL, … }` shape is rewritten to fixed ports (3478/443) and an IP
 * host into `a-b-c-d.edge.agora.io`, while an array is used verbatim.
 */
export function applyRtcProxy(client: unknown, config: RtcProxyConfig): boolean {
  const c = client as ProxyCapableClient;
  if (typeof c.setProxyServer !== 'function' || typeof c.setTurnServer !== 'function') return false;
  try {
    c.setProxyServer(config.signalingDomain);
    c.setTurnServer(config.iceServers.map(s => ({ urls: s.urls, username: s.username, credential: s.credential })));
    return true;
  } catch {
    // INVALID_OPERATION: the client is not DISCONNECTED (or cloud proxy is on).
    return false;
  }
}

/**
 * Leaves the half-joined client and prepares it for a proxied attempt. The SDK
 * keeps the proxy settings across `leave()`, and they may only be set while
 * DISCONNECTED — hence leave first.
 */
export async function switchClientToProxy(client: { leave: () => Promise<void> }, config: RtcProxyConfig): Promise<boolean> {
  await client.leave().catch(() => undefined);
  return applyRtcProxy(client, config);
}

/**
 * One join with the fallback, for screens without their own retry loop (lives).
 * Starts on the proxy when remembered/forced; a direct join that fails on the
 * path is retried once through the proxy. `report` receives the telemetry
 * moments so each surface keeps its own event prefix.
 */
export async function joinWithRtcProxyFallback(
  client: { leave: () => Promise<void> },
  join: () => Promise<unknown>,
  report: (event: 'proxy_mode', props: Record<string, unknown>) => void,
  isDisposed: () => boolean = () => false,
): Promise<{ viaProxy: boolean }> {
  let viaProxy = false;
  const state = await readRtcProxyState();
  const startReason = initialProxyReason(state, Date.now());
  if (startReason) {
    const config = await fetchRtcProxyConfig();
    if (config && applyRtcProxy(client, config)) {
      viaProxy = true;
      report('proxy_mode', { mode: 'proxy', reason: startReason, relayOnly: !!state.relayOnly, domain: config.signalingDomain });
    }
  }
  try {
    await join();
    return { viaProxy };
  } catch (err) {
    if (isDisposed()) throw err;
    if (viaProxy) {
      await forgetRtcProxy();
      report('proxy_mode', { mode: 'direct', reason: 'proxy_join_failed', code: (err as { code?: unknown })?.code ?? null });
      throw err;
    }
    if (!isPathFailure(err)) throw err;
    const config = await fetchRtcProxyConfig();
    if (!config || isDisposed() || !(await switchClientToProxy(client, config))) throw err;
    report('proxy_mode', { mode: 'proxy', reason: 'join_failed', code: (err as { code?: unknown })?.code ?? null, domain: config.signalingDomain });
    try {
      await join();
    } catch (proxyErr) {
      await forgetRtcProxy();
      report('proxy_mode', { mode: 'direct', reason: 'proxy_join_failed', code: (proxyErr as { code?: unknown })?.code ?? null });
      throw proxyErr;
    }
    await rememberRtcProxy();
    return { viaProxy: true };
  }
}

/** Secondary clients (screen share, practice clip) follow the main one's path. */
export async function applyRtcProxyIfActive(client: unknown): Promise<boolean> {
  const state = await readRtcProxyState();
  if (!initialProxyReason(state, Date.now())) return false;
  const config = await fetchRtcProxyConfig();
  return !!config && applyRtcProxy(client, config);
}

// ─── dev/staging console switch ──────────────────────────────────────────────

const RELAY_ONLY_MARK = '__agapeRtcRelayOnly';

/**
 * Media only through our TURN. The SDK does not apply relay-only to an ICE
 * server array, so for QA the page's RTCPeerConnection is told directly.
 * dev/staging only; a reload restores the original constructor.
 *
 * Both entry points are covered: the SDK pre-creates its media connection and
 * hands it the real configuration later through `setConfiguration` (checked on
 * dev 15.09: the constructor alone left the media connection on `all`, and it
 * went out over srflx while its two helper connections were `relay`).
 */
export function installRelayOnly(target: any = typeof window !== 'undefined' ? window : undefined): void {
  const Original = target?.RTCPeerConnection;
  if (!Original || Original[RELAY_ONLY_MARK]) return;
  const Patched = function RTCPeerConnectionRelayOnly(this: unknown, config?: RTCConfiguration, ...rest: unknown[]) {
    return new Original({ ...(config ?? {}), iceTransportPolicy: 'relay' }, ...rest);
  } as any;
  Patched.prototype = Original.prototype;
  Object.setPrototypeOf(Patched, Original);
  Patched[RELAY_ONLY_MARK] = true;
  const originalSet = Original.prototype?.setConfiguration;
  if (typeof originalSet === 'function' && !originalSet[RELAY_ONLY_MARK]) {
    const relaySet = function setConfigurationRelayOnly(this: unknown, config?: RTCConfiguration) {
      return originalSet.call(this, { ...(config ?? {}), iceTransportPolicy: 'relay' });
    } as any;
    relaySet[RELAY_ONLY_MARK] = true;
    Original.prototype.setConfiguration = relaySet;
  }
  target.RTCPeerConnection = Patched;
}

function notify(): void {
  for (const l of Array.from(listeners)) {
    try { l(); } catch { /* one screen's failure must not block the others */ }
  }
}

/**
 * `window.agapeRtcProxy` on dev and staging (never on agapella.io):
 *
 *   agapeRtcProxy.force()                    — signaling + media via the proxy
 *   agapeRtcProxy.force({ relayOnly: true }) — and media ONLY via our TURN
 *   agapeRtcProxy.off()                      — back to direct, memory cleared
 *   agapeRtcProxy.status()                   — what the next join will do
 *
 * An open room rebuilds its connection at once; a live picks it up on the next
 * join (reload the page). The backend honours the force only where
 * RtcProxy__AllowForce is on (dev/staging).
 */
export function installRtcProxyConsole(hostname: string): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  if (!hostname || hostname.includes('agapella.io')) return;
  const w = window as any;
  if (w.agapeRtcProxy) return;

  readRtcProxyState().then(state => { if (state.forced && state.relayOnly) installRelayOnly(); });

  w.agapeRtcProxy = {
    async force(opts?: { relayOnly?: boolean }) {
      const relayOnly = !!opts?.relayOnly;
      await writeRtcProxyState({ forced: true, relayOnly });
      cachedConfig = null;
      noConfigUntil = 0;
      const config = await fetchRtcProxyConfig();
      if (!config) {
        console.warn('[rtc-proxy] backend returned no config (204): the proxy is not set up on this stand');
        return 'no-config';
      }
      if (relayOnly) installRelayOnly();
      notify();
      console.info(`[rtc-proxy] forced via ${config.signalingDomain}${relayOnly ? ', media via TURN only' : ''}. An open room reconnects by itself; reload the page for a Live.`);
      return 'forced';
    },
    async off() {
      await writeRtcProxyState({});
      cachedConfig = null;
      notify();
      console.info('[rtc-proxy] off: direct path. Reload the page to drop relayOnly.');
      return 'direct';
    },
    async status() {
      const state = await readRtcProxyState();
      const reason = initialProxyReason(state, Date.now());
      return {
        nextJoin: reason ? 'proxy' : 'direct',
        reason,
        relayOnly: !!state.relayOnly,
        rememberedUntil: state.rememberedUntil ? new Date(state.rememberedUntil).toISOString() : null,
        signalingDomain: cachedConfig?.signalingDomain ?? null,
      };
    },
  };
}
